import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/config/constants';
import {
  getActiveSessionRevision, getAuthTokenForRequest, subscribeActiveSessionRevision,
} from '@/api/authenticatedClient';
import { getDeliveryLog } from '@/api/deliveries';
import { getRuns } from '@/api/runs';

const empty = () => ({ rows: [], loading: false, accepted: false, error: null });
const loadOrders = (scope, config) => getDeliveryLog({
  ...(scope.date ? { date: scope.date } : {}),
  ...(scope.name.trim() ? { name: scope.name.trim() } : {}),
  ...(scope.address.trim() ? { address: scope.address.trim() } : {}),
}, config);
const loadRuns = (scope, config) => getRuns(scope.date, config);

// Each read family has a separate generation. Retrying one query may retain
// that query's accepted rows, but changing query/session never borrows rows.
function useOwnedRead(scope, enabled, request, message, debounce = false) {
  const liveScope = useRef(scope);
  liveScope.current = scope;
  const cycleRef = useRef(null);
  const priorScope = useRef(null);
  const [view, setView] = useState(empty);
  const owns = useCallback(cycle => Boolean(cycle?.active && cycleRef.current === cycle
    && liveScope.current === cycle.scope
    && cycle.scope.revision === getActiveSessionRevision()), []);
  const publish = useCallback(cycle => {
    if (owns(cycle)) setView({ ...cycle.view, scope: cycle.scope, cycle, sequence: cycle.sequence });
  }, [owns]);
  const read = useCallback(async cycle => {
    if (!owns(cycle) || !cycle.enabled) return;
    clearTimeout(cycle.timer);
    cycle.controller?.abort();
    const controller = new AbortController();
    cycle.controller = controller;
    const sequence = ++cycle.sequence;
    const current = () => owns(cycle) && cycle.sequence === sequence;
    cycle.view.loading = true;
    cycle.view.error = null;
    publish(cycle);
    try {
      // Capture credentials at admission, never after asynchronous preparation.
      const token = getAuthTokenForRequest(`${API}/delivery-log`);
      if (!token || !cycle.scope.binding?.user
          || cycle.scope.binding.revision !== cycle.scope.revision) {
        throw new Error('No current authorized session');
      }
      const rows = await request(cycle.scope, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
      if (!current()) return;
      cycle.view.rows = rows;
      cycle.view.accepted = true;
    } catch {
      if (!current()) return;
      cycle.view.error = message;
    } finally {
      if (current()) {
        cycle.view.loading = false;
        publish(cycle);
      }
    }
  }, [message, owns, publish, request]);
  useEffect(() => {
    const previous = priorScope.current;
    priorScope.current = scope;
    const cycle = { scope, enabled, active: true, sequence: 0,
      view: { ...empty(), loading: enabled } };
    cycleRef.current = cycle;
    publish(cycle);
    const delay = debounce && previous?.date === scope.date
      && previous?.revision === scope.revision && previous?.binding === scope.binding ? 350 : 0;
    if (enabled) {
      if (delay) cycle.timer = setTimeout(() => { void read(cycle); }, delay);
      else void read(cycle);
    }
    return () => {
      cycle.active = false;
      clearTimeout(cycle.timer);
      cycle.controller?.abort();
    };
  }, [scope, enabled, debounce, publish, read]);
  const retry = useCallback(() => {
    const cycle = cycleRef.current;
    if (cycle?.scope === scope) return read(cycle);
    return Promise.resolve();
  }, [read, scope]);
  const visible = view.scope === scope ? view : { ...empty(), loading: enabled };
  // A rendered snapshot is an admission receipt, not just a query key. Retry
  // increments synchronously, before React publishes its loading render, and
  // permanently retires callbacks captured from the preceding generation.
  const isCurrent = useCallback(() => Boolean(owns(view.cycle)
    && view.scope === scope && view.sequence === view.cycle.sequence), [owns, scope, view]);
  return { ...visible, retry, isCurrent };
}

export default function useDeliveryLog({ selectedDate, nameSearch = '', addressSearch = '' }) {
  const { sessionBinding } = useAuth();
  const revision = useSyncExternalStore(
    subscribeActiveSessionRevision, getActiveSessionRevision, getActiveSessionRevision,
  );
  const runOwner = useMemo(() => ({ date: selectedDate, binding: sessionBinding, revision }),
    [selectedDate, sessionBinding, revision]);
  const owner = useMemo(() => ({ ...runOwner, name: nameSearch, address: addressSearch }),
    [runOwner, nameSearch, addressSearch]);
  const liveOwner = useRef(owner);
  liveOwner.current = owner;
  const lifetime = useRef(null);
  useEffect(() => {
    const current = { active: true };
    lifetime.current = current;
    return () => { current.active = false; };
  }, []);
  const idle = !selectedDate && !nameSearch.trim() && !addressSearch.trim();
  const orders = useOwnedRead(owner, !idle, loadOrders, 'Could not load delivery records.', true);
  const runs = useOwnedRead(runOwner, Boolean(selectedDate), loadRuns, 'Could not load runs.');
  const retryOrders = orders.retry;
  const retryRuns = runs.retry;
  const ordersCurrent = orders.isCurrent;
  const runsCurrent = runs.isCurrent;
  const retry = useCallback(() => Promise.all([retryOrders(), retryRuns()]), [retryOrders, retryRuns]);
  const captureSession = useCallback(() => {
    const capturedLifetime = lifetime.current;
    const current = () => Boolean(capturedLifetime?.active && lifetime.current === capturedLifetime
      && liveOwner.current === owner && getActiveSessionRevision() === revision
      && sessionBinding?.user && sessionBinding.revision === revision
      && ordersCurrent() && runsCurrent());
    if (!current()) return null;
    const token = getAuthTokenForRequest(`${API}/delivery-log`);
    return token ? { token, revision, isCurrent: current } : null;
  }, [owner, revision, sessionBinding, ordersCurrent, runsCurrent]);
  const stale = Boolean((orders.accepted && orders.error) || (runs.accepted && runs.error));
  const loading = orders.loading || runs.loading;
  const isCurrent = useCallback(() => Boolean(captureSession()), [captureSession]);
  return {
    orders: orders.rows, runs: runs.rows, orderLoading: orders.loading, runLoading: runs.loading,
    loading, updating: loading, orderError: orders.error, runError: runs.error, stale, idle,
    readyToPrint: Boolean(selectedDate && orders.accepted && runs.accepted && !loading
      && !orders.error && !runs.error && sessionBinding?.user && sessionBinding.revision === revision),
    retry, owner, revision, captureSession, isCurrent,
  };
}
